// =====================================================
// MetodologiaScoring.js
// Panel de ayuda metodológica para el Scoring Agropecuario.
// Alineado con ScoringService y agro.umbral_alerta_scoring.
// src/pages/Agro/Productor/MetodologiaScoring.js
// =====================================================

import React, { useState } from "react"
import { Card, CardBody, CardHeader, Collapse, Button } from "reactstrap"
import SiembrasVigentesNota from "./SiembrasVigentesNota"

const P = {
  verde: { text: "#1a7431", border: "#34a853" },
  ambar: { text: "#854d0e", border: "#f59e0b" },
  teal: { text: "#115e59", border: "#0d9488" },
  violeta: { text: "#5b21b6", border: "#8b5cf6" },
  azul: { text: "#1e40af", border: "#3b82f6" },
  rojo: { text: "#7f1d1d", border: "#b91c1c" },
  gris: { text: "#334155", border: "#64748b" },
}

// Los pesos salen de las constantes de ScoringService y suman 100%.
const DIMENSIONES = [
  {
    nombre: "Historial productivo",
    peso: "20%",
    mide: "Cuántas siembras tienen rendimiento real registrado y qué proporción alcanzó al menos el 80% del rendimiento esperado.",
  },
  {
    nombre: "Escala productiva",
    peso: "15%",
    mide: "Superficie total, cantidad de establecimientos y stock ganadero. Mide si la explotación tiene tamaño suficiente para absorber una mala campaña.",
  },
  {
    nombre: "Condición actual",
    peso: "15%",
    mide: "NDVI promedio de los lotes y estado declarado de los cultivos en pie.",
  },
  {
    nombre: "Riesgo climático",
    peso: "15%",
    mide: "Diversificación geográfica de la cartera y alertas meteorológicas oficiales vigentes sobre los establecimientos.",
  },
  {
    nombre: "Rentabilidad",
    peso: "15%",
    mide: "Margen bruto por hectárea de la actividad proyectada.",
  },
  {
    nombre: "Cobertura de riesgos",
    peso: "10%",
    mide: "Porcentaje de la superficie sembrada con seguro contratado.",
  },
  {
    nombre: "Tenencia de tierra",
    peso: "10%",
    mide: "Proporción de superficie propia sobre el total. La tierra propia es garantía y reduce el costo de arrendamiento.",
  },
]

const MetodologiaScoring = () => {
  const [show, setShow] = useState(false)

  return (
    <div className="mb-3 text-muted">
      <Button
        color="light"
        size="sm"
        outline
        onClick={() => setShow(!show)}
        className="border"
      >
        <i className="mdi mdi-help-circle-outline me-1" />
        {show ? "Ocultar metodología" : "¿Cómo se calcula el score?"}
      </Button>

      <Collapse isOpen={show}>
        <Card className="mt-2 mb-0 border-0 shadow-sm overflow-hidden">
          <div style={{ height: "3px", background: P.violeta.border }} />
          <CardHeader className="bg-transparent py-2">
            <span className="fw-semibold">
              Metodología del scoring agropecuario
            </span>
            <small className="text-muted d-block">
              Los umbrales de alerta se configuran en{" "}
              <strong>Administración &gt; Proyección Crediticia</strong>
            </small>
          </CardHeader>

          <CardBody style={{ fontSize: "0.88rem" }}>
            <SiembrasVigentesNota />
            <Seccion
              titulo="1. Qué mide el score"
              color={P.violeta.text}
              border={P.violeta.border}
            >
              <p className="mb-2">
                Un puntaje de 0 a 100 que resume el perfil de riesgo productivo
                del productor. Es el promedio ponderado de siete dimensiones,
                cada una calculada de 0 a 100 por separado.
              </p>
              <Formula>
                <strong>Score total</strong> = Σ (Score dimensión × Peso)
              </Formula>
              <p className="mb-0 mt-2" style={{ fontSize: "0.82rem" }}>
                <strong>El score no es un dictamen crediticio.</strong> Describe
                la operación productiva; la decisión de crédito la toma el
                analista con este dato, la capacidad de pago y el resto del
                legajo.
              </p>
            </Seccion>

            <Seccion
              titulo="2. Las siete dimensiones"
              color={P.azul.text}
              border={P.azul.border}
            >
              <div className="table-responsive">
                <table
                  className="table table-sm mb-0"
                  style={{ fontSize: "0.82rem" }}
                >
                  <thead>
                    <tr>
                      <th>Dimensión</th>
                      <th className="text-center">Peso</th>
                      <th>Qué mide</th>
                    </tr>
                  </thead>
                  <tbody>
                    {DIMENSIONES.map(d => (
                      <tr key={d.nombre}>
                        <td className="fw-semibold">{d.nombre}</td>
                        <td className="text-center">{d.peso}</td>
                        <td>{d.mide}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Seccion>

            <Seccion
              titulo="3. Categoría de riesgo"
              color={P.verde.text}
              border={P.verde.border}
            >
              <p className="mb-2">
                El score total se traduce a una categoría, que es lo que se
                muestra en el encabezado del informe.
              </p>
              <div className="d-flex gap-2 flex-wrap mb-2">
                <Pastilla texto="A — Excelente · 80 a 100" color={P.verde} />
                <Pastilla texto="B — Bueno · 65 a 79" color={P.azul} />
                <Pastilla texto="C — Regular · 50 a 64" color={P.ambar} />
                <Pastilla texto="D — Observado · 35 a 49" color={P.rojo} />
                <Pastilla texto="E — Crítico · menos de 35" color={P.rojo} />
              </div>
              <p className="mb-0" style={{ fontSize: "0.82rem" }}>
                Los cortes son fijos y no dependen del perfil productivo: lo que
                cambia por perfil son los umbrales de las alertas, no la escala
                del score.
              </p>
            </Seccion>

            <Seccion
              titulo="4. Perfil productivo"
              color={P.teal.text}
              border={P.teal.border}
            >
              <p className="mb-1">
                Los mismos umbrales no sirven para una explotación extensiva
                pampeana y para un viñedo bajo riego. El sistema resuelve un
                perfil según la provincia del establecimiento:
              </p>
              <ul className="mb-1" style={{ paddingLeft: "1.2rem" }}>
                <li>
                  <strong>Cuyo perenne</strong> — San Juan, Mendoza y La Rioja.
                  Vid, olivo y frutales bajo riego.
                </li>
                <li>
                  <strong>General</strong> — el resto del país. Agricultura
                  extensiva.
                </li>
              </ul>
              <p className="mb-0" style={{ fontSize: "0.82rem" }}>
                Un viñedo comercial de 10 ha es una explotación normal, no una
                de escala reducida: con el umbral del perfil general quedaría
                marcado. Por eso el perfil cambia los cortes, no el cálculo.
              </p>
            </Seccion>

            <Seccion
              titulo="5. Alertas"
              color={P.ambar.text}
              border={P.ambar.border}
            >
              <p className="mb-1">
                Cada dimensión puede emitir alertas de nivel{" "}
                <strong>ATENCIÓN</strong> o <strong>CRÍTICO</strong>. Los cortes
                que las disparan no están escritos en el código: salen de una
                tabla parametrizable por perfil, zona y cultivo, y se pueden
                cambiar sin desplegar una versión nueva.
              </p>
              <p className="mb-1">
                Entre ellas está el{" "}
                <strong>contraste del rendimiento declarado</strong> contra la
                serie oficial del MAGyP para el departamento del
                establecimiento. Se evalúa solo sobre valores que cargó una
                persona: sobre un rendimiento sugerido por el sistema, la
                comparación no diría nada.
              </p>
              <Ref>
                Una alerta señala algo para mirar, no una descalificación. El
                informe deja registrado el umbral aplicado y contra qué se
                comparó.
              </Ref>
            </Seccion>

            <Seccion
              titulo="6. Qué pasa si falta un dato"
              color={P.gris.text}
              border={P.gris.border}
              sinBorde
            >
              <p className="mb-0" style={{ fontSize: "0.82rem" }}>
                La ausencia de un dato no se computa como riesgo. Un productor
                sin historial de siembras cargado no recibe el puntaje de un
                productor con mal historial: la dimensión queda en un valor
                neutro y el informe declara que no había información. Confundir
                «no medido» con «malo» sería el error más caro que puede cometer
                un modelo de scoring.
              </p>
            </Seccion>
          </CardBody>
        </Card>
      </Collapse>
    </div>
  )
}

// Sub-componentes

const Seccion = ({ titulo, color, border, children, sinBorde }) => (
  <div
    className={sinBorde ? "mb-2" : "mb-3 pb-3"}
    style={sinBorde ? {} : { borderBottom: "1px solid #f1f5f9" }}
  >
    <div className="d-flex align-items-center gap-2 mb-2">
      <div
        style={{
          width: 3,
          height: 16,
          borderRadius: 2,
          backgroundColor: border || color,
        }}
      />
      <span className="fw-semibold" style={{ color }}>
        {titulo}
      </span>
    </div>
    <div style={{ paddingLeft: "0.7rem" }}>{children}</div>
  </div>
)

const Formula = ({ children }) => (
  <pre
    className="mb-1 p-2 rounded"
    style={{
      backgroundColor: "#f8fafc",
      border: "1px solid #e2e8f0",
      fontSize: "0.8rem",
      whiteSpace: "pre-wrap",
    }}
  >
    {children}
  </pre>
)

const Ref = ({ children }) => (
  <div className="mt-1" style={{ fontSize: "0.78rem", color: "#64748b" }}>
    <i className="mdi mdi-information-outline me-1" />
    {children}
  </div>
)

const Pastilla = ({ texto, color }) => (
  <span
    className="px-2 py-0 rounded-pill"
    style={{
      fontSize: "0.72rem",
      backgroundColor: "#f8fafc",
      color: color.text,
      border: `1px solid ${color.border}40`,
    }}
  >
    {texto}
  </span>
)

export default MetodologiaScoring
